import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import FieldError from '@/forms/FieldError';
import { Props, RejectionFormPayload, RejectionFormValues } from '@/forms/RejectionForm/types';
import useIntakeRejectionReasons from '@/hooks/useIntakeRejectionReasons';
import ProgramRepository from '@/repositories/ProgramRepository';
import Button from '@/ui/components/Button';
import InputGroup from '@/ui/components/Form/InputGroup';
import Label from '@/ui/components/Form/Label';
import Textarea from '@/ui/components/Form/Textarea';
import Heading from '@/ui/components/Heading';
import Spinner from '@/ui/components/Spinner';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const RejectionForm = ({ onSuccess, personId, programQualificationId }: Props) => {
  const { addMessage, currentUser } = useContext(AppContext);
  const queryClient = useQueryClient();
  const programRepository = ProgramRepository();
  const { data: intakeRejectionReasons, isPending: isLoadingIntakeRejectionReasons } =
    useIntakeRejectionReasons();

  const initialValues: RejectionFormValues = {
    intake_rejection_reasons: [],
    note: '',
    rejected_by: currentUser?.id
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register
  } = useForm({ defaultValues: initialValues });

  const reject = useMutation({
    mutationFn: ({ values }: { values: RejectionFormPayload }) => {
      return programRepository.rejectPerson({ params: values, programQualificationId });
    },
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Successfully rejected person' });
      personId && (await queryClient.invalidateQueries({ queryKey: ['getPerson', personId] }));
      onSuccess && onSuccess();
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submitForm = (values: RejectionFormValues) => {
    const payload: RejectionFormPayload = {
      ...values,
      note: values.note ?? ''
    };
    reject.mutate({ values: payload });
  };

  return (
    <div>
      <Heading value={3} text="Why should this person not be enrolled?" />

      <form onSubmit={handleSubmit(submitForm)} data-testid="rejection-form">
        <InputGroup>
          {isLoadingIntakeRejectionReasons ? (
            <Spinner label="Loading rejection reasons" />
          ) : intakeRejectionReasons ? (
            <>
              <Label htmlFor="" text="Rejection Reason">
                {Object.values(intakeRejectionReasons).map((intakeRejectionReason) => {
                  const id = `intake-rejection-reason-${intakeRejectionReason.id}`;

                  return (
                    <div key={id}>
                      <Label htmlFor={id} reverse text={intakeRejectionReason.name}>
                        <input
                          {...register('intake_rejection_reasons', {
                            required: 'At least one rejection reason is required'
                          })}
                          className="mr-1 mb-1"
                          id={id}
                          type="checkbox"
                          value={intakeRejectionReason.name}
                        />
                      </Label>
                    </div>
                  );
                })}
              </Label>
            </>
          ) : null}

          <FieldError message={errors.intake_rejection_reasons?.message} />
        </InputGroup>

        <InputGroup>
          <Controller
            control={control}
            name="note"
            render={({ field }) => {
              return (
                <Textarea
                  id="id-note"
                  labelText="Note"
                  name={field.name}
                  onChange={field.onChange}
                  placeholder="Type any additional info for this rejection"
                />
              );
            }}
          />

          <FieldError message={errors.note?.message} />
        </InputGroup>

        <InputGroup>
          <Button disabled={reject.isPending} type="submit">
            {reject.isPending ? <Spinner color={Color.WHITE} label="Submitting..." /> : 'Submit'}
          </Button>
        </InputGroup>
      </form>
    </div>
  );
};

export default RejectionForm;
